import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_current_production_table';

export const ASSET_CURRENT_PRODUCTION_QUERY = gql`
  query assetCurrentProduction($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      currentDate
      estimatedCurrentDate
      formattedRecentProdLiquidsBbld
      formattedRecentProdGasMmcfd
    }
  }
`;

interface AssetCurrentProductionRow {
  label: string;
  value: string;
}

const formatProductionDate = (date?: string | null): string | null => {
  if (!date) {
    return null;
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const formatted = parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return formatted.replace(' ', '-');
};

const formatCurrentProduction = (asset: Asset | null | undefined): AssetCurrentProductionRow[] => {
  const productionDate =
    formatProductionDate(asset?.currentDate) ??
    formatProductionDate(asset?.estimatedCurrentDate) ??
    'N/A';

  const liquidsProduction = asset?.formattedRecentProdLiquidsBbld ?? 'N/A';
  const gasProduction = asset?.formattedRecentProdGasMmcfd ?? 'N/A';

  return [
    {
      label: `Liquids Production (${productionDate}) (bbl/d)`,
      value: liquidsProduction,
    },
    {
      label: `Gas Production (${productionDate}) (MMcf/d)`,
      value: gasProduction,
    },
  ];
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const shouldSkipQuery = !!persistedData || !assetId;
  const {
    data: queryData,
    loading,
    error,
  } = useQuery<{ assetV2: Asset }>(ASSET_CURRENT_PRODUCTION_QUERY, {
    variables: { input: { id: assetId } },
    skip: shouldSkipQuery,
  });

  useEffect(() => {
    if (shouldSkipQuery || !queryData?.assetV2) {
      return;
    }

    const data = queryData.assetV2;
    setDataFetched(data);
    if (onComponentLoad) {
      onComponentLoad({
        componentId: COMPONENT_ID,
        data,
      });
    }
  }, [shouldSkipQuery, queryData, onComponentLoad]);

  return { data: dataFetched ?? ({} as Asset), error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<Asset>) => {
  const rows = formatCurrentProduction(data);

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
      data-testid='asset-current-production-table'
    >
      <ComponentTable tableLayout='auto'>
        <ComponentTableHeader>Current Production</ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const AssetCurrentProductionTableComponent: ComponentDefinition<Asset> = {
  id: COMPONENT_ID,
  name: 'Asset Current Production',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
